import React from 'react';
import {
  Box,
  RichText,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  VStack,
} from '@wegroup/design-system';
import { handleResponseLanguage } from '../../../../../../../i18n';
import useFlowInputComponent from '@flows/src/hooks/useFlowsInputComponent';
import { FlowsQuestion } from '@flows/src/types/FlowsTypes';

interface Props {
  question: FlowsQuestion;
}

/**
 * DisplayInputComponent renders HTML content without expecting user input.
 */
const DisplayInputComponent = ({ question }: Props) => {
  // Use the flow input component hook to maintain flow state consistency
  useFlowInputComponent({ question });

  const title = handleResponseLanguage(question.title);
  // Get the HTML content from description using the standard language handler
  const htmlContent = handleResponseLanguage(question.description);

  // If no content, render nothing
  if (!htmlContent || !title) {
    return null;
  }

  const isCollapsed = question.rules.options?.display_collapsed === true;

  // If collapsed mode is enabled, render with accordion
  if (isCollapsed) {
    return (
      <DisplayInputComponentAccordion title={title} subtitle={htmlContent} />
    );
  }

  // Default behavior: show title and content directly
  return (
    <VStack width="100%" p={4} gap={4} alignItems="flex-start">
      {title && <RichText value={title} />}
      {htmlContent && <RichText value={htmlContent} />}
    </VStack>
  );
};

export default DisplayInputComponent;

export const DisplayInputComponentAccordion = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) => {
  return (
    <Box width="100%">
      {/* Invisible subtitle to reserve horizontal space - keeps height 0 but affects width */}
      <Box visibility="hidden" height={0} overflow="hidden" width="100%">
        <RichText value={subtitle} />
      </Box>
      {/* Visible accordion */}
      <Accordion defaultIndex={[]} allowToggle>
        <AccordionItem>
          <AccordionButton>
            <Box flex="1" textAlign="left">
              <RichText value={title} />
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <RichText value={subtitle} />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};
